import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { engine } from '../data/content';
const EASE = [0.23, 1, 0.32, 1] as const;
export function Engine() {
  const [active, setActive] = useState(0);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const parts = engine.parts;
  const current = parts[active];
  const select = (index: number, focus = false) => {
    const next = (index + parts.length) % parts.length;
    setActive(next);
    if (focus) tabRefs.current[next]?.focus();
  };
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      select(active + 1, true);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      select(active - 1, true);
    } else if (event.key === 'Home') {
      event.preventDefault();
      select(0, true);
    } else if (event.key === 'End') {
      event.preventDefault();
      select(parts.length - 1, true);
    }
  };
  return <section id="engine" className="w-full border-t border-line bg-ink py-24 lg:py-32" data-id="element-13" data-mp="1ni57zpw2n937">
      <div className="mx-auto max-w-shell px-5 lg:px-8" data-id="element-14" data-mp="1rhw66enxgrhr">
        <header className="mx-auto max-w-3xl text-center" data-id="element-15" data-mp="1wi0o894hhua3">
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-brand-green" data-id="element-16" data-mp="1wjlkc36g5la2">
            {engine.eyebrow}
          </p>
          <h2 className="display mt-4 text-[clamp(2.2rem,5.4vw,4rem)] leading-[0.96] text-cream" data-id="element-17" data-mp="21fako8qaj67f">
            {engine.headingLead} <span className="text-brand-green" data-id="element-18" data-mp="2b76nty78hmii">{engine.headingAccent}</span>
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-cream/70" data-id="element-19" data-mp="2f8ibvzaglzol">{engine.intro}</p>
        </header>

        <div className="mt-14 grid gap-8 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] lg:items-stretch" data-id="element-20" data-mp="2k7uzwnd2hcsr">
          {/* Diagram */}
          <div data-id="element-21" data-mp="2p7r4ootl989d">
            <div className="relative overflow-hidden rounded-lg border border-line bg-black" data-id="element-22" data-mp="2p77kt2kdo9sp">
              <img src={engine.image} alt="A black sedan with its hood up, beside the seven components that make it drive: engine, transmission, suspension, brake system, steering system, ECU and battery." className="h-full w-full object-cover" data-id="element-23" data-mp="p4zphf20jh6d" />

              <div role="tablist" aria-label="Car parts" onKeyDown={onKeyDown} className="absolute inset-0" data-id="element-24" data-mp="u2897k2t7t9q">
                {parts.map((part, i) => {
                const isActive = i === active;
                return <button key={part.part} ref={el => tabRefs.current[i] = el} role="tab" id={`part-tab-${i}`} aria-selected={isActive} aria-controls="part-panel" tabIndex={isActive ? 0 : -1} onClick={() => select(i)} onMouseEnter={() => select(i)} style={{
                  left: `${part.hotspot.x}%`,
                  top: `${part.hotspot.y}%`
                }} className="group absolute -translate-x-1/2 -translate-y-1/2 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-brand-green focus-visible:ring-offset-2 focus-visible:ring-offset-black" data-id="element-25" data-mp="1hr18nqin4j93" data-mpi={i}>
                      <span className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-bold transition-[background-color,border-color,color,transform] duration-150 ease-out ${isActive ? 'scale-110 border-brand-green bg-brand-green text-ink' : 'border-cream/40 bg-black/60 text-cream/80 backdrop-blur group-hover:border-brand-green group-hover:text-cream'}`} data-id="element-26" data-mp="1bbaktnpkiuqd">
                        {i + 1}
                      </span>
                      <span className="sr-only" data-id="element-27" data-mp="10or7mwe4q8zu">{part.part}</span>
                    </button>;
              })}
              </div>
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-x-2 gap-y-2" data-id="element-28" data-mp="rnax8onbe6vm">
              {parts.map((part, i) => {
              const isActive = i === active;
              return <button key={part.part} type="button" onClick={() => select(i)} className={`rounded-md border px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-[background-color,border-color,color] duration-150 ease-out ${isActive ? 'border-brand-green bg-brand-green/10 text-brand-green' : 'border-line text-cream/55 hover:border-cream/30 hover:text-cream/85'}`} data-id="element-29" data-mp="hrpxkx5pxip1" data-mpi={i}>
                    {i + 1}. {part.part}
                  </button>;
            })}
            </div>
            <p className="mt-4 text-sm text-cream/45" data-id="element-30" data-mp="3l0mh5vu7qe0k">{engine.hint}</p>
          </div>

          {/* Detail panel */}
          <div id="part-panel" role="tabpanel" aria-labelledby={`part-tab-${active}`} className="flex flex-col rounded-lg border border-line bg-surface p-8 lg:p-10" data-id="element-31" data-mp="3q1a3i47hy6og">
            <div className="flex items-center justify-between" data-id="element-32" data-mp="2xa8ktt4lht06">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-brand-green" data-id="element-33" data-mp="32bxzrjuxuqtj">
                Part {active + 1} of {parts.length}
              </span>
              <span className="display text-5xl leading-none text-cream/10" data-id="element-34" data-mp="3c1hk6tyausqi">
                {String(active + 1).padStart(2, '0')}
              </span>
            </div>

            <AnimatePresence mode="wait" data-id="element-35" data-mp="2ejr4ko0y8pvp">
              <motion.div key={current.part} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} className="mt-6" data-id="element-36" data-mp="2jjn0s13end0p">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cream/45" data-id="element-37" data-mp="2l8h1t3u9ur2u">
                  {current.part}
                </p>
                <h3 className="display mt-3 text-[clamp(1.6rem,2.6vw,2.25rem)] leading-tight text-cream" data-id="element-38" data-mp="1sl4prnlqm1ns">
                  {current.title}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-cream/70" data-id="element-39" data-mp="1nnl07rjla2gj">{current.body}</p>
              </motion.div>
            </AnimatePresence>

            <div className="mt-auto flex items-center gap-3 pt-10" data-id="element-40" data-mp="1xkhaa1p4ywn7">
              <button type="button" onClick={() => select(active - 1)} aria-label="Previous part" className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-line text-cream transition-colors duration-150 ease-out hover:border-cream/40 hover:bg-white/5" data-id="element-41" data-mp="1xmalzvelw722">
                <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-42" data-mp="3lj05yxtza4qb" />
              </button>
              <button type="button" onClick={() => select(active + 1)} aria-label="Next part" className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-line text-cream transition-colors duration-150 ease-out hover:border-cream/40 hover:bg-white/5" data-id="element-43" data-mp="37j8zrj9pdsmv">
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-44" data-mp="84wyylovagtz" />
              </button>
              <div className="ml-auto flex gap-1.5" aria-hidden="true" data-id="element-45" data-mp="3qf8siwai907i">
                {parts.map((part, i) => <span key={part.part} className={`h-1.5 rounded-full transition-[width,background-color] duration-150 ease-out ${i === active ? 'w-6 bg-brand-green' : 'w-1.5 bg-cream/20'}`} data-id="element-46" data-mp="m33mb7w6wyfe" data-mpi={i} />)}
              </div>
            </div>
          </div>
        </div>

        <p className="display mt-16 text-center text-[clamp(1.35rem,3.2vw,2.35rem)] leading-tight text-cream" data-id="element-47" data-mp="3tfqtlh267l0y">
          {engine.closer.lead} <span className="text-brand-green" data-id="element-48" data-mp="3jjxi8106txkr">{engine.closer.accent}</span>{' '}
          {engine.closer.rest}
        </p>
      </div>
    </section>;
}